import { ParseTreeVisitor } from 'antlr4';
import AioExpressionParser from '../anltr/AioExpressionParser.js';
import AioExpressionParsingException from '../exception/AioExpressionParsingException.js';
/**
 * A visitor class for evaluating AIO expression language expressions.
 */
class AioExpressionEvalVisitor extends ParseTreeVisitor {
    static CONCAT_EMO_COMPETITOR_NAMES = 'CONCAT_EMO_COMPETITOR_NAMES';
    static EVENT_MARKET_OUTCOME_NAME = 'EVENT_MARKET_OUTCOME_NAME';
    static OUTCOME_COMPETITOR_TEAM_NAME = 'O.C.T.NAME';
    static OUTCOME_COMPETITOR_PLAYER_NAME = 'O.C.P.NAME';
    variables = new Map();
    /**
     * Constructs the event market outcome competitor name by replacing placeholders
     * in the given template with corresponding values from the competitorName object.
     *
     * @private
     * @static
     * @param {EventMarketOutcomeNamesDTO} competitorName - Object containing event market outcome name details.
     * @param {string} eventMarketOutcomeNameDisplayNamesTemplate - Template string with placeholders to be replaced.
     * @returns {string} - The formatted event market outcome competitor name.
     */
    static constructEventMarketOutcomeCompetitorName(competitorName, eventMarketOutcomeNameDisplayNamesTemplate) {
        if (eventMarketOutcomeNameDisplayNamesTemplate.includes(this.EVENT_MARKET_OUTCOME_NAME))
            eventMarketOutcomeNameDisplayNamesTemplate =
                eventMarketOutcomeNameDisplayNamesTemplate.replace(this.EVENT_MARKET_OUTCOME_NAME, competitorName.eventMarketOutcomeName);
        if (eventMarketOutcomeNameDisplayNamesTemplate.includes(this.OUTCOME_COMPETITOR_TEAM_NAME))
            eventMarketOutcomeNameDisplayNamesTemplate =
                eventMarketOutcomeNameDisplayNamesTemplate.replace(this.OUTCOME_COMPETITOR_TEAM_NAME, competitorName.teamName);
        if (eventMarketOutcomeNameDisplayNamesTemplate.includes(this.OUTCOME_COMPETITOR_PLAYER_NAME))
            eventMarketOutcomeNameDisplayNamesTemplate =
                eventMarketOutcomeNameDisplayNamesTemplate.replace(this.OUTCOME_COMPETITOR_PLAYER_NAME, competitorName.playerName);
        return eventMarketOutcomeNameDisplayNamesTemplate;
    }
    /**
     * Sets the variables to be used during evaluation.
     *
     * @param variables - A map of variable names and their values.
     * @returns The current instance of the visitor.
     */
    setVariables(variables) {
        this.variables = variables;
        return this;
    }
    /**
     * Visits an `InputContext` and evaluates the contained expressions.
     *
     * @param ctx - The `InputContext` to visit.
     * @returns The concatenated result of evaluating child elements.
     */
    visitInput(ctx) {
        return ctx
            .element_list()
            .map((elementCtx) => this.visit(elementCtx))
            .filter((res) => res !== null)
            .join('');
    }
    /**
     * Visits an `ElementContext` and retrieves the text content.
     *
     * @param ctx - The `ElementContext` to visit.
     * @returns The text content or null if it's not a text element.
     */
    visitElement(ctx) {
        const dynamicExprCtx = ctx.dynamicExpr();
        const textToken = ctx.TEXT();
        if (textToken !== null)
            return textToken.getText();
        if (dynamicExprCtx !== null)
            return this.visit(dynamicExprCtx);
        return null;
    }
    /**
     * Visits a `DynamicExprContext` and evaluates the contained statements.
     *
     * @param ctx - The `DynamicExprContext` to visit.
     * @returns The concatenated result of evaluating dynamic statements.
     */
    visitDynamicExpr(ctx) {
        const result = [];
        for (const statementCtx of ctx.statement_list()) {
            const res = this.visit(statementCtx);
            if (res !== null)
                result.push(res.toString());
        }
        return result.length > 0 ? result.join('') : null;
    }
    /**
     * Visits a `StatementContext` and evaluates the contained expression.
     *
     * @param ctx - The `StatementContext` to visit.
     * @returns The result of evaluating the statement's expression.
     */
    visitStatement(ctx) {
        const exprCtx = ctx.expr();
        return exprCtx ? this.visit(exprCtx) : null;
    }
    /**
     * Visits an `IdContext` and retrieves the value of a variable.
     *
     * @param ctx - The `IdContext` to visit.
     * @returns The value of the variable.
     * @throws {AioExpressionParsingException} If the variable is undefined.
     */
    visitId(ctx) {
        const id = ctx.getText();
        const variable = this.variables.get(id);
        if (variable !== undefined && variable !== 'undefined')
            return variable;
        throw new AioExpressionParsingException(`Undefined variable: ${id}`);
    }
    /**
     * Visits a `NumberContext` and parses the numeric value.
     *
     * @param ctx - The `NumberContext` to visit.
     * @returns The parsed numeric value.
     */
    visitNumber(ctx) {
        return parseFloat(ctx.NUMBER().getText());
    }
    /**
     * Visits a `ParensContext` and evaluates the expression inside parentheses.
     *
     * @param ctx - The `ParensContext` to visit.
     * @returns The result of evaluating the expression inside parentheses.
     */
    visitParens(ctx) {
        return this.visit(ctx.expr());
    }
    /**
     * Visits a `MulDivContext` and performs multiplication or division.
     *
     * @param ctx - The `MulDivContext` to visit.
     * @returns The result of the multiplication or division.
     */
    visitMulDiv(ctx) {
        const left = parseFloat(this.visit(ctx.expr(0)).toString());
        const right = parseFloat(this.visit(ctx.expr(1)).toString());
        if (ctx._op.type === AioExpressionParser.MUL)
            return left * right;
        else
            return left / right;
    }
    /**
     * Visits an `AddSubContext` and performs addition or subtraction.
     *
     * @param ctx - The `AddSubContext` to visit.
     * @returns The result of the addition or subtraction.
     */
    visitAddSub(ctx) {
        const left = parseFloat(this.visit(ctx.expr(0)).toString());
        const right = parseFloat(this.visit(ctx.expr(1)).toString());
        if (ctx._op.type === AioExpressionParser.ADD)
            return left + right;
        else
            return left - right;
    }
    /**
     * Visits a `ModuloContext` and performs the modulo operation.
     *
     * @param ctx - The `ModuloContext` to visit.
     * @returns The result of the modulo operation.
     */
    visitModulo(ctx) {
        const dividend = parseFloat(this.visit(ctx.expr(0)).toString());
        const divisor = parseFloat(this.visit(ctx.expr(1)).toString());
        return dividend % divisor;
    }
    /**
     * Visits a `RelationalContext` and performs relational operations.
     *
     * @param ctx - The `RelationalContext` to visit.
     * @returns The result of the relational operation (boolean).
     * @throws {AioExpressionParsingException} If the operation is unsupported.
     */
    visitRelational(ctx) {
        const left = parseFloat(this.visit(ctx.expr(0)).toString());
        const right = parseFloat(this.visit(ctx.expr(1)).toString());
        switch (ctx._op.type) {
            case AioExpressionParser.LT:
                return left < right;
            case AioExpressionParser.LTE:
                return left <= right;
            case AioExpressionParser.GT:
                return left > right;
            case AioExpressionParser.GTE:
                return left >= right;
            case AioExpressionParser.EQUALS:
                return left === right;
            case AioExpressionParser.NOT_EQUALS:
                return left !== right;
            default:
                throw new AioExpressionParsingException(`Unsupported operation: ${ctx._op.type}`);
        }
    }
    /**
     * Visits a `LogicalContext` and performs logical operations.
     *
     * @param ctx - The `LogicalContext` to visit.
     * @returns The result of the logical operation (boolean).
     */
    visitLogical(ctx) {
        const left = JSON.parse(this.visit(ctx.expr(0)).toString().toLowerCase());
        const right = JSON.parse(this.visit(ctx.expr(1)).toString().toLowerCase());
        if (ctx._op.type === AioExpressionParser.AND)
            return left && right;
        return left || right;
    }
    /**
     * Visits a `NotContext` and performs a logical NOT operation.
     *
     * @param ctx - The `NotContext` to visit.
     * @returns The result of the logical NOT operation (boolean).
     */
    visitNot(ctx) {
        const value = parseFloat(this.visit(ctx.expr()).toString());
        return value === 0;
    }
    /**
     * Concatenates competitor names based on the provided context, using a separator
     * and placeholders for event market outcome names.
     *
     * @param {ConcatEmoCompetitorNamesContext} ctx - The context containing the separator and placeholders.
     * @returns {string} - A concatenated string of formatted competitor names.
     * @throws {AioExpressionParsingException} - Throws if competitor names are not provided.
     */
    visitConcatEmoCompetitorNames(ctx) {
        const separator = ctx.STRING(0).toString().replace(/"/g, '');
        const eventMarketOutcomeNamePlaceHolders = ctx.STRING(1).toString().replace(/"/g, '');
        const competitorNames = this.variables.get(AioExpressionEvalVisitor.CONCAT_EMO_COMPETITOR_NAMES);
        if (!competitorNames)
            throw new AioExpressionParsingException('Competitor names not provided for DISPLAY_ALL_EMO_NAMES_VALUES function!');
        const names = [];
        for (const eventMarketOutcomeCompetitorNames of competitorNames)
            names.push(AioExpressionEvalVisitor.constructEventMarketOutcomeCompetitorName(eventMarketOutcomeCompetitorNames, eventMarketOutcomeNamePlaceHolders));
        return names.join(separator);
    }
    /**
     * Visits a `FormatNumberDigitsContext` and formats a number with a specified number of digits.
     *
     * @param ctx - The `FormatNumberDigitsContext` to visit.
     * @returns The formatted number as a string.
     */
    visitFormatNumberDigits(ctx) {
        const value = parseFloat(this.visit(ctx.expr(0)).toString());
        const digits = Math.floor(parseFloat(this.visit(ctx.expr(1)).toString()));
        const factor = Math.pow(10, digits);
        const truncatedValue = (value < 0 ? Math.ceil : Math.floor)(value * factor) / factor;
        return digits > 0 ? truncatedValue.toFixed(digits) : Math.floor(truncatedValue).toString();
    }
    /**
     * Visits a `DisplayIfContext` and returns a string if the condition is true, otherwise null.
     *
     * @param ctx - The `DisplayIfContext` to visit.
     * @returns The string value if the condition is true, otherwise null.
     */
    visitDisplayIf(ctx) {
        const result = this.visit(ctx.expr());
        if (typeof result !== 'boolean' || !result) {
            return null;
        }
        return ctx.stringArg().getText().replace(/"/g, '');
    }
    /**
     * Visits a `SubstringContext` and extracts a substring from a string.
     *
     * @param ctx - The `SubstringContext` to visit.
     * @returns The extracted substring.
     */
    visitSubstring(ctx) {
        const string = this.visit(ctx.expr());
        const from = parseInt(ctx.numberArg(0).getText()) - 1;
        const to = parseInt(ctx.numberArg(1).getText()) - 1;
        return string.slice(from, to + 1);
    }
}
export default AioExpressionEvalVisitor;
